import React, { useState } from 'react';
import { Card, CardHeader, CardContent } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/badge';
import { Icon } from '@/shared/icons';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { useCards } from '../hooks';
import { AddPaymentMethodDialog } from './AddPaymentMethodDialog';
import type { AddCardRequest, CompanyCardResponse } from '../types';

/**
 * Grid of payment cards with management actions.
 * Uses useCards() hook for CRUD operations.
 */
export const CardsManager: React.FC = () => {
  const { cards, isLoading, addCard, isAdding, deleteCard, isDeleting, setDefaultCard, isSettingDefault } = useCards();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const handleAddCard = (data: { nickname: string; holderName: string; number: string; expiryMonth: number; expiryYear: number; cvv: string }) => {
    const request: AddCardRequest = {
      nickname: data.nickname,
      holderName: data.holderName,
      number: data.number,
      expiryMonth: data.expiryMonth,
      expiryYear: data.expiryYear,
      cvv: data.cvv,
    };
    addCard(request, {
      onSuccess: () => setShowAddDialog(false),
    });
  };

  const handleDelete = (index: number) => {
    setDeletingIndex(index);
  };

  const confirmDelete = (index: number) => {
    deleteCard(index, {
      onSuccess: () => setDeletingIndex(null),
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton width="200px" height={24} />
          <Skeleton width="160px" height={36} variant="rectangular" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} variant="card" height="160px" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — só ação (o título vem da aba ativa) */}
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowAddDialog(true)}>
          <Icon name="plus" className="h-4 w-4 mr-2" />
          Adicionar Cartao
        </Button>
      </div>

      {/* Cards grid */}
      {!cards || cards.length === 0 ? (
        <Card>
          <CardContent className="px-4 pb-4 pt-8">
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-3"
                style={{ backgroundColor: 'var(--theme-card-border)' }}
              >
                <Icon name="credit-card" className="h-6 w-6" style={{ color: 'var(--theme-muted-text)' }} />
              </div>
              <p
                className="text-sm font-medium"
                style={{ color: 'var(--theme-card-text)' }}
              >
                Nenhum cartao cadastrado
              </p>
              <p
                className="text-xs mt-1"
                style={{ color: 'var(--theme-muted-text)' }}
              >
                Adicione um cartao para realizar pagamentos
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cards.map((card: CompanyCardResponse, index: number) => (
            <Card key={`${card.maskedNumber}-${index}`}>
              <CardHeader className="pb-2 py-3 px-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon
                      name="credit-card"
                      className="h-5 w-5"
                      style={{ color: '#8b5cf6' }}
                    />
                    <span
                      className="font-mono text-sm font-medium"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      **** {card.maskedNumber.slice(-4)}
                    </span>
                  </div>
                  {card.isDefault && (
                    <Badge variant="default" dot>Principal</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'var(--theme-muted-text)' }}>Bandeira</span>
                    <span
                      className="font-medium capitalize"
                      style={{ color: 'var(--theme-card-text)' }}
                    >
                      {card.brand}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'var(--theme-muted-text)' }}>Validade</span>
                    <span style={{ color: 'var(--theme-card-text)' }}>
                      {String(card.expiryMonth).padStart(2, '0')}/{card.expiryYear}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: 'var(--theme-muted-text)' }}>Titular</span>
                    <span style={{ color: 'var(--theme-card-text)' }}>{card.holderName}</span>
                  </div>
                  {card.nickname && (
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: 'var(--theme-muted-text)' }}>Apelido</span>
                      <span style={{ color: 'var(--theme-card-text)' }}>{card.nickname}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 pt-3 border-t" style={{ borderColor: 'var(--theme-card-border)' }}>
                  {!card.isDefault && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setDefaultCard(index)}
                      loading={isSettingDefault}
                    >
                      Tornar Principal
                    </Button>
                  )}
                  {deletingIndex === index ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="error"
                        size="sm"
                        onClick={() => confirmDelete(index)}
                        loading={isDeleting}
                      >
                        Confirmar
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setDeletingIndex(null)}
                      >
                        Cancelar
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleDelete(index)}
                    >
                      <Icon name="trash" className="h-3.5 w-3.5 mr-1" />
                      Remover
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add card dialog */}
      <AddPaymentMethodDialog
        isOpen={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onSubmit={handleAddCard}
        isLoading={isAdding}
      />
    </div>
  );
};

CardsManager.displayName = 'CardsManager';
